import { escapeHtml } from '../ui.js';
import { listSharedDrives, listFolder, listChildren, isFolderMime } from '../google-api.js';
import { folderCode } from '../drive-folders.js';
import { isIgnoredFile } from '../doc-convention.js';
import { openFile } from './google-panels.js';
import { renderFolderView } from './files-folder.js';
import { swr, driveKey, ageText, seedCache } from '../drive-cache.js';

// FILES › Shared drives (Karan 06/10/2026). The drives as they sit in Google:
//
//   1  every shared drive THIS person's Google account can open — access is
//      Google's, not Track's roles — with its items, access and last change;
//   2  a project drive (Projects, Archive, ADIPS: most top-level folders are
//      project numbers) lists its project folders, most recent first, with a
//      search; any other drive lists its top-level folders and files;
//   3  a project folder's own folders, each counted.
//   …  any folder below that opens IN Track (files-folder.js) — breadcrumb,
//      subfolders, files — and a file opens in Track's viewers. Google Drive is
//      the "Open Drive" button or a folder's "Open in Drive" link, never a row.
//
// SPEED (Karan 06/10: "it takes ages to load drive"). Each level paints from
// what Drive said last time (drive-cache.js, per person) and is read again
// behind it; the counts are ONE Drive call for every row on screen
// (listChildren / walkTree), not one per row.
//
// User-facing text says "project", never "job" (Karan 06/10). The back link
// sits where a table's note would. Nothing is written. Every value from Drive
// is escaped; every href goes through safeUrl().

const pad2 = (n) => String(n).padStart(2, '0');
const PAGE = 50;
const parseJob = (name) => { const m = String(name || '').match(/^(A\d{4}|\d{4})\b/); return m ? m[1] : null; };
const skel = (w, h) => `<span class="fm-skel" style="width:${w};height:${h};"></span>`;

function dayShort(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}${d.getFullYear() === now.getFullYear() ? '' : `/${String(d.getFullYear()).slice(-2)}`}`;
}

// What each drive is for, from the drive list Karan corrected on 09/07. A drive
// this does not know shows no description rather than a guess.
const DESCRIBE = [
  [/project/i, 'Projects 5000 and up'], [/archive/i, 'Projects before 5000'], [/adips/i, 'ADIPS projects'],
  [/enquir/i, 'Enquiries before a project is won'], [/admin/i, 'Finance, HR and the office'],
  [/brand/i, 'Logos, templates and print'], [/resource/i, 'Codes, references and standard details'],
  [/shared/i, 'Shared across the team'],
];
export const describeDrive = (name) => (DESCRIBE.find(([re]) => re.test(String(name || ''))) || [null, ''])[1];
export function accessOf(caps = {}) {
  if (caps.canEdit || caps.canAddChildren) return 'Can edit';
  if (caps.canComment) return 'Can comment';
  return 'Can view';
}
/** A drive is a project drive when most of its top-level folders are project numbers. */
export function isJobDrive(items) {
  const dirs = (items || []).filter((f) => isFolderMime(f.mimeType));
  return dirs.length >= 3 && dirs.filter((f) => parseJob(f.name)).length / dirs.length >= 0.6;
}
const latestOf = (items) => (items || []).reduce((m, f) => (String(f.modifiedTime || '') > m ? String(f.modifiedTime) : m), '');

// Small concurrency pool — the drive totals are filled in after the table paints.
async function pool(items, n, fn) {
  let i = 0;
  await Promise.all(Array.from({ length: Math.min(n, items.length) }, async () => { while (i < items.length) { const k = i++; await fn(items[k], k); } }));
}

// v4 (p15): where you are is a breadcrumb — Shared drives / PROJECTS / … — with
// the table's note at its end. `path` = [{ label, depth }], the last one is here.
const tableHead = (path, note) => `<nav class="crumbs fl4-crumbs" aria-label="Where you are">
    ${path.map((c, i) => (i < path.length - 1
    ? `<button type="button" data-depth="${c.depth}">${escapeHtml(c.label)}</button><span aria-hidden="true">/</span>`
    : `<span class="fl4-here" aria-current="page">${escapeHtml(c.label)}</span>`)).join('')}
    <span class="fl4-crumb-end"><span class="fl4-note" id="fx-browse-note">${escapeHtml(note)}</span></span>
  </nav>`;

// Where you were survives a refresh (Karan 18/10: "refreshing whilst in a
// project kicks me back"). The trail is kept per tab in sessionStorage and
// read back only when the page was reloaded; arriving from the menu still
// starts at all drives.
const PLACE_KEY = 'fx-drive-place';
export function savedPlace() {
  try { const s = JSON.parse(sessionStorage.getItem(PLACE_KEY) || '[]'); return Array.isArray(s) ? s : []; } catch (_) { return []; }
}

export async function renderDrives(body, session, { projects = [], setHero = () => {}, onCheckJob = null, restore = false } = {}) {
  const who = session && session.email;
  const byNumber = new Map(projects.map((p) => [String(p.number || ''), p]));
  const stack = restore ? savedPlace() : [];   // [{ kind:'drive', drive }, { kind:'job', drive, folder }, { kind:'folder', … }]
  const save = () => { try { sessionStorage.setItem(PLACE_KEY, JSON.stringify(stack)); } catch (_) { /* private mode */ } };
  let drives = [];
  let view = 0;              // bumps on every navigation, so a late answer never paints over the next level
  const live = () => body.isConnected;

  // A folder's listing: from the cache at once, then Drive. onData may run twice.
  const listed = (id, onData, onError) => swr({ key: driveKey(who, 'list', id), fetch: () => listFolder(id), onData, onError });
  const cachedNote = (meta) => (meta && meta.cached && meta.revalidating ? `What Drive showed ${ageText(meta.at)} · checking again` : '');

  body.innerHTML = `<div class="fx-table">${[0, 1, 2, 3, 4].map(() => `<div class="fx-row fx-g-drv">${skel('40%', '13px')}${skel('30px', '12px')}${skel('70px', '12px')}${skel('40px', '12px')}</div>`).join('')}</div>`;

  // The crumbs above a level: Shared drives, then each level below it.
  const labelOf = (e) => (e.kind === 'drive' ? e.drive.name : e.kind === 'job' ? e.folder.name : ((e.trail || [])[0] || {}).name || '');
  const pathTo = (depth) => [{ label: 'Shared drives', depth: 0 }, ...stack.slice(0, depth).map((e, k) => ({ label: labelOf(e), depth: k + 1 }))];
  const beforeOf = (depth) => pathTo(depth).map((c) => ({ label: c.label, onClick: () => { stack.length = c.depth; paint(); } }));
  const paint = () => {
    view++;
    save();
    const top = stack[stack.length - 1];
    if (!top) return level1();
    if (top.kind === 'drive') return level2(top.drive);
    // A project folder and everything below it: the folder view, with the
    // preview panel (p15). Its own trail goes deeper; the crumbs lead back up.
    const job = top.kind === 'job';
    return renderFolderView(body, {
      session, setHero, preview: true, driveInHead: true,
      trail: job ? (top.trail || [{ id: top.folder.id, name: top.folder.name, webViewLink: top.folder.webViewLink }]) : top.trail,
      project: job ? parseJob(top.folder.name) : top.project, codeAt: job ? -1 : top.codeAt,
      onTrail: (t) => { top.trail = t; save(); },
      before: beforeOf(stack.length - 1),
      onCheckJob: job ? onCheckJob : null,
    });
  };
  const push = (entry) => { stack.push(entry); paint(); };

  // ── Level 1: all drives ───────────────────────────────────────────────────
  function level1() {
    const v = view;
    const drawDrives = (meta) => {
      if (!live() || v !== view) return;
      setHero({ driveHref: 'https://drive.google.com/drive/shared-drives' });
      body.innerHTML = `<div class="fx-table">
          ${tableHead([], cachedNote(meta) || 'Access follows your Google permissions, not Track roles.')}
          <div class="fx-cols fx-g-drv"><span>Shared drive</span><span class="r">Items</span><span>Access</span><span class="r">Updated</span></div>
          ${drives.length ? drives.map((d, i) => `<div class="fx-row fx-g-drv" data-go="drive" data-i="${i}" role="button" tabindex="0">
              <span class="fx-cell"><span class="fx-stack"><span class="fx-t">${escapeHtml(d.name)}</span>${describeDrive(d.name) ? `<span class="fx-sub">${escapeHtml(describeDrive(d.name))}</span>` : ''}</span></span>
              <span class="fx-cell r fx-mono fx-c-items" data-items="${i}">·</span>
              <span class="fx-cell">${escapeHtml(accessOf(d.capabilities))}</span>
              <span class="fx-cell r fx-mono fx-c-upd" data-upd="${i}">·</span>
            </div>`).join('') : '<div class="fx-msg" style="padding-top:14px;">Your Google account cannot open any shared drives.</div>'}
        </div>`;
      wireRows('[data-go="drive"]', (r) => push({ kind: 'drive', drive: drives[Number(r.dataset.i)] }));
      // Items = what sits at the top of the drive; Updated = its newest change.
      // Each drive's top level is cached too — it is level 2's listing.
      pool(drives, 4, async (d, i) => {
        await listed(d.id, (items) => {
          if (!live() || v !== view) return;
          const a = body.querySelector(`[data-items="${i}"]`); if (a) a.textContent = items.length.toLocaleString('en-GB');
          const b = body.querySelector(`[data-upd="${i}"]`); if (b) b.textContent = dayShort(latestOf(items));
        }).catch(() => { const a = body.querySelector(`[data-items="${i}"]`); if (a) a.textContent = '—'; });
      });
    };
    return swr({
      key: driveKey(who, 'drives'),
      fetch: () => listSharedDrives(),
      onData: (list, meta) => { drives = list; drawDrives(meta); },
      onError: () => { const n = body.querySelector('#fx-browse-note'); if (n) n.textContent = 'Drive could not be reached; this is the list from last time.'; },
    }).catch((e) => {
      if (!live() || v !== view) return;
      body.innerHTML = `<div class="fx-msg">Google Drive could not list your shared drives: ${escapeHtml((e && e.message) || 'unknown error')}. Reconnect and try again.</div>`;
    });
  }

  // ── Level 2: one drive ────────────────────────────────────────────────────
  function level2(drive) {
    const v = view;
    setHero({ driveHref: `https://drive.google.com/drive/folders/${encodeURIComponent(drive.id)}` });
    body.innerHTML = `<div class="fx-table">${tableHead(pathTo(1), 'Reading the drive…')}${[0, 1, 2, 3].map(() => `<div class="fx-row fx-g-job">${skel('40px', '12px')}${skel('50%', '13px')}${skel('24px', '12px')}${skel('40px', '12px')}</div>`).join('')}</div>`;
    wireBack();
    let state = null;   // keep the search box's text across a revalidated repaint
    return listed(drive.id, (items, meta) => {
      if (!live() || v !== view) return;
      if (isJobDrive(items)) { state = jobList(drive, items, meta, state); return; }
      otherDrive(drive, items, meta);
    }, () => { const n = body.querySelector('#fx-browse-note'); if (n) n.textContent = 'Drive could not be reached; this is what it showed last time.'; })
      .catch((e) => {
        if (!live() || v !== view) return;
        body.innerHTML = `<div class="fx-table">${tableHead(pathTo(1), '')}<div class="fx-msg">This drive could not be read: ${escapeHtml((e && e.message) || 'unknown error')}.</div></div>`;
        wireBack();
      });
  }

  // Any other drive: its top-level folders and files. Folders open in Track.
  function otherDrive(drive, items, meta) {
    const v = view;
    const rows = items.filter((f) => !isIgnoredFile(f.name)).sort((a, b) => (isFolderMime(b.mimeType) - isFolderMime(a.mimeType)) || String(a.name).localeCompare(String(b.name)));
    const dirs = rows.filter((f) => isFolderMime(f.mimeType));
    body.innerHTML = `<div class="fx-table">
        ${tableHead(pathTo(1), cachedNote(meta) || describeDrive(drive.name))}
        <div class="fx-cols fx-g-sub"><span>Folder</span><span class="r">Items</span><span class="r">Updated</span></div>
        ${rows.map((f, i) => `<div class="fx-row fx-g-sub" data-go="item" data-i="${i}" role="button" tabindex="0">
            <span class="fx-cell"><span class="fx-ico${isFolderMime(f.mimeType) ? ' fx-ico-dir' : ''}" aria-hidden="true"></span><span class="fx-t">${escapeHtml(f.name)}</span></span>
            <span class="fx-cell r fx-mono" data-n="${escapeHtml(f.id)}">${isFolderMime(f.mimeType) ? '·' : ''}</span>
            <span class="fx-cell r fx-mono fx-c-upd">${escapeHtml(dayShort(f.modifiedTime))}</span>
          </div>`).join('') || '<div class="fx-msg" style="padding-top:14px;">This drive is empty.</div>'}
      </div>`;
    wireBack();
    wireRows('[data-go="item"]', (r) => openItem(rows[Number(r.dataset.i)], { homeLabel: drive.name, siblings: rows }));
    fillCounts(dirs, v);
  }

  // Every folder on screen counted in ONE Drive call. The answer IS each
  // folder's listing, so it goes in the cache: opening one paints at once.
  function fillCounts(dirs, v) {
    if (!dirs.length) return;
    listChildren(dirs.map((f) => f.id)).then((m) => {
      seedCache(who, m);
      if (!live() || v !== view) return;
      dirs.forEach((f) => {
        const c = body.querySelector(`[data-n="${CSS.escape(f.id)}"]`);
        const kids = m.get(f.id);
        if (c) c.textContent = kids ? String(kids.filter((k) => !isIgnoredFile(k.name)).length) : '—';
      });
    }).catch(() => { /* leave the dots */ });
  }

  // A project drive: project folders, most recent first, searchable.
  function jobList(drive, items, meta, prior) {
    const v = view;
    const jobsAll = items.filter((f) => isFolderMime(f.mimeType) && parseJob(f.name))
      .sort((a, b) => String(b.modifiedTime || '').localeCompare(String(a.modifiedTime || '')));
    const keep = prior && prior.q ? prior.q.value : '';
    body.innerHTML = `<div class="fx-table">
        ${tableHead(pathTo(1), cachedNote(meta) || `${jobsAll.length.toLocaleString('en-GB')} project folders, most recent first`)}
        <label class="fx-search"><input type="search" id="fx-drive-q" placeholder="Search ${escapeHtml(drive.name)} by project number, client or site" aria-label="Search this drive" value="${escapeHtml(keep)}"></label>
        <div class="fx-cols fx-g-job"><span>Project</span><span>Folder</span><span class="r">Items</span><span class="r">Updated</span></div>
        <div id="fx-jobs"></div>
        <div class="fx-foot" id="fx-jobs-foot"></div>
      </div>`;
    wireBack();
    const host = body.querySelector('#fx-jobs');
    const foot = body.querySelector('#fx-jobs-foot');
    const q = body.querySelector('#fx-drive-q');
    const draw = () => {
      const needle = q.value.trim().toLowerCase();
      const hits = jobsAll.filter((f) => {
        if (!needle) return true;
        const p = byNumber.get(parseJob(f.name));
        const site = p && p.siteAddress ? `${p.siteAddress.line1 || ''} ${p.siteAddress.postcode || ''}` : '';
        return `${f.name} ${site}`.toLowerCase().includes(needle);
      });
      const shown = hits.slice(0, PAGE);
      host.innerHTML = shown.map((f, i) => {
        const job = parseJob(f.name);
        const p = byNumber.get(job);
        const rest = String(f.name).slice(job.length).replace(/^[\s\-–—·]+/, '');
        const site = p && p.siteAddress && p.siteAddress.line1 ? p.siteAddress.line1 : '';
        return `<div class="fx-row fx-g-job" data-go="job" data-i="${i}" role="button" tabindex="0">
            <span class="fx-cell fx-mono fx-blue">${escapeHtml(job)}</span>
            <span class="fx-cell"><span class="fx-stack"><span class="fx-t">${escapeHtml(rest || f.name)}</span>${site ? `<span class="fx-sub fx-t">${escapeHtml(site)}</span>` : ''}</span></span>
            <span class="fx-cell r fx-mono fx-c-items" data-n="${escapeHtml(f.id)}">·</span>
            <span class="fx-cell r fx-mono">${escapeHtml(dayShort(f.modifiedTime))}</span>
          </div>`;
      }).join('') || '<div class="fx-msg" style="padding-top:14px;">No project folder matches.</div>';
      foot.textContent = hits.length > PAGE ? `Showing ${PAGE} of ${hits.length.toLocaleString('en-GB')}. Search to find the rest.` : '';
      host.querySelectorAll('[data-go="job"]').forEach((r) => {
        const go = () => push({ kind: 'job', drive, folder: shown[Number(r.dataset.i)] });
        r.addEventListener('click', go);
        r.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } });
      });
      fillCounts(shown, v);
    };
    let deb = null;
    q.addEventListener('input', () => { clearTimeout(deb); deb = setTimeout(draw, 120); });
    draw();
    return { q };
  }

  // A row below the drive level: a folder opens in Track, a file in a viewer.
  function openItem(f, { homeLabel, project = null, projectFolder = false, siblings = null } = {}) {
    if (!f) return;
    if (isFolderMime(f.mimeType)) {
      // Under a project folder, a numbered folder ("4. Calculations") carries
      // document control in the folder view.
      const codeAt = projectFolder && folderCode(f.name) ? 0 : -1;
      push({ kind: 'folder', trail: [{ id: f.id, name: f.name, webViewLink: f.webViewLink }], project, codeAt, homeLabel });   // homeLabel: kept for a saved place from before v4
      return;
    }
    const reviewerFor = (g) => ({ reference: g.name, title: g.name, status: 'Current', user: (session && (session.displayName || session.email)) || '', email: (session && session.email) || null, projectNo: String(project || '') });
    openFile(f, { reviewer: reviewerFor(f), reviewerFor, ...(siblings && { siblings }) });
  }

  function wireRows(sel, fn) {
    body.querySelectorAll(sel).forEach((r) => {
      r.addEventListener('click', () => fn(r));
      r.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(r); } });
    });
  }

  function wireBack() {
    body.querySelectorAll('[data-depth]').forEach((b) => b.addEventListener('click', () => { stack.length = Number(b.dataset.depth); paint(); }));
  }

  paint();
}

// The old entry point (5a "All drives") now lands on the same tab.
export const renderBrowse = (mount, session, opts = {}) => renderDrives(mount, session, opts);
